import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Typography, Divider, Tabs, Row, Col, Space, Alert, Spin, Grid, Flex } from 'antd';
import { HistoryOutlined, TruckOutlined, FileTextOutlined } from '@ant-design/icons';
import SubHeader from '../components/common/SubHeader';
import LogWoodPODetail from '../components/log-wood-po/LogWoodPODetail';
import LogWoodPOSummary from '../components/log-wood-po/LogWoodPOSummary';
import ActionButtons from '../components/workflow/ActionButtons';
import HistoryListModal from '../components/workflow/HistoryListModal';
import TimeoutModal from '../components/common/TimeoutModal';
import { defaultLogWoodPOWorkflowDetail } from '../constance/dataTypes';
import { URL_WORKFLOW } from '../constance/urls';
import useWorkflowDetail from '../hooks/useWorkflowDetail';
import { PATH } from '../routes/Routes';
import { Toast } from '../utils/Toast';
import { useIdleTimer } from 'react-idle-timer';
import { Media } from "../AppMedia";
import ColorButton from '../components/common/ColorButton';
import { BLUE_COLOR } from '../constance/color';
import { useWindowDimensionContext } from '../hooks/WindowDimensionProvider';

const LogWoodPODetailView = () => {
  const { Title } = Typography;
  const { t } = useTranslation();
  const params = useParams();
  const history = useHistory();

  const [detail, setDetail] = useState(defaultLogWoodPOWorkflowDetail);
  const [detailTab, setDetailTab] = useState('truck_transaction_detail');
  const [isTimeout, setIsTimeout] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const detailRef = useRef(null);
  const [currentDetail, setCurrentDetail] = React.useState(defaultLogWoodPOWorkflowDetail);
  const { size } =  useWindowDimensionContext();

  const containerWidth = {
    xs: '100%',
    sm: 723,
    md: 933,
    lg: 1127,
    xl: 1127,
    xxl: 1127,
  }

  const {
    response,
    fetchData,
    handleAction,
    isLoading,
    errorMessages,
  } = useWorkflowDetail({
    workflowUrl: URL_WORKFLOW.LOG_WOOD_PO_WORKFLOW,
    id: params.id,
    onSuccess: (action) => {
      history.push(PATH.LOG_WOOD_PO);
      Toast.success(`${t(`workflows.actions.${action}`)} ${t('success')}`);
    },
  });

  useIdleTimer({
    timeout: 1000 * 60 * 30, // 30 minutes
    onIdle: () => setIsTimeout(true),
    crossTab: true,
  });

  useEffect(() => {
    if (!params.id) history.push(PATH.LOG_WOOD_PO);
  }, []);

  useEffect(() => {
    setDetail({ ...defaultLogWoodPOWorkflowDetail, ...response });
  }, [response]);


  React.useEffect(() => {
    if (detailRef.current) {
      setCurrentDetail(detailRef.current.detail)
    }
  }, [detailTab, detailRef])

  const handleSaveWorkflowDetail = async () => {
    setIsActionLoading(true);
    if (!detailRef.current) return false;
    const isSuccess = await detailRef.current.handleSaveDetail();
    if (isSuccess) {
      Toast.success(`${t('saving')} ${t('success')}`);
      fetchData();
    } else {
      Toast.error(`${t('saving')} ${t('error')}`);
    }
    setIsActionLoading(false);
    return isSuccess;
  };

  const handleDoAction = async (params) => {
    if (detail.workflow_params.formEditable) {
      const isSuccess = await handleSaveWorkflowDetail();
      if (!isSuccess) {
        return;
      }
    }
    const isActionSuccess = await handleAction(params);
    if (!isActionSuccess) {
      Toast.error(`${t(`workflows.actions.${params.action}`)} ${t('error')}`);
    }
  };

  return (
    <div>
      <SubHeader />
      <div style={{ width: containerWidth[size], margin: "auto", marginTop: "1rem",  }}>
      {/* <Space direction="vertical" style={{ maxWidth: containerWidth[size] }}> */}
        <TimeoutModal
          open={isTimeout}
          onClose={() => history.push(PATH.HOME)}
        />

        <Spin spinning={isLoading} fullscreen size='large' />

        <Space direction="vertical" style={{ width: '100%', textAlign: 'left', marginBottom: '1em' }}>
          {errorMessages && (
            <Alert
              message={t('error')}
              description={errorMessages}
              type="error"
              showIcon
            />
          )}
          {detail.memo && (
            <Alert
              message={t('memo')}
              description={detail.memo}
              type="warning"
              showIcon
            />
          )}
        </Space>

        <Flex justify="space-between">
          <Title style={{ marginTop: 0 }} level={3}> {t('log_wood_po.detail')} - [{detail.state_name}]</Title>
          <ColorButton
            variant="outlined"
            color='primary'
            override={BLUE_COLOR}
            icon={<HistoryOutlined />}
            onClick={() => setIsHistoryModalVisible(true)}
          >
            {t('workflows.history')}
          </ColorButton>
        </Flex>

        <Tabs
          defaultActiveKey="truck_transaction_detail"
          activeKey={detailTab}
          onChange={setDetailTab}
          items={[
            {
              key: 'truck_transaction_detail',
              label: <span><TruckOutlined /> {t('log_wood_po.truck_transaction_detail')}</span>,
              children: (
                <LogWoodPODetail
                  ref={detailRef}
                  enableEdit={detail.workflow_params.formEditable}
                  truckTransaction={detail.truck_transaction}
                  logWoodEvaluationData={detail.log_wood_evaluation_data}
                  active={detailTab === 'truck_transaction_detail'}
                  setDetailLoading={(value) => setIsDetailLoading(value)}/>
              )
            },
            {
              key: 'summary',
              label: <span><FileTextOutlined /> {t('log_wood_po.summary')}</span>,
              children: (
                <LogWoodPOSummary
                  detail={detail}
                  active={detailTab === 'summary'}/>
              )
            }
          ]}
        />

        <div style={{ marginTop: '1rem' }}>
          <ActionButtons
            enableSave={detail.workflow_params.formEditable}
            handleSave={() => handleSaveWorkflowDetail()}
            actions={detail.workflow_actions.filter(e => e !== 'cancel')}
            handleAction={(action) => handleDoAction(action)}
            isLoading={isLoading || isActionLoading || isDetailLoading}
          />
        </div>

        <HistoryListModal
          open={isHistoryModalVisible}
          onClose={() => setIsHistoryModalVisible(false)}
          workflowHistoryUrl={`${URL_WORKFLOW.LOG_WOOD_PO_WORKFLOW_HISTORY}${params.id}`}
        />
      </div>
    </div>
  );
};

export default LogWoodPODetailView;
